import { getProgress } from "utils/common/video";
import type { GetUserThemesQuery, UserTutoringMinimumFragment } from "api/generated";

import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

export interface ThemeProgress {
  videoCount: number;
  videoFinished: number;
  fixedCount: number;
  fixedFinished: number;
  othersCount: number;
  othersFinished: number;
}

export interface ThemeRowDataType {
  themeId?: string;
  tutoringId: string;
  title: string;
  description?: string;
  avgPoints?: number;
  themeProgress: ThemeProgress;
  assignmentsRed: number;
  assignmentsGreen: number;
  assignmentsYellow: number;
  isBadge: boolean;
}

const countProgress = (tutoringData: UserTutoringMinimumFragment, email: string): ThemeProgress => {
  const videoFinished = tutoringData.videotips
    .map(tip => tip.videoId)
    .filter(vid => {
      const progress = getProgress(vid, email);
      return progress?.isViewed === true;
    }).length;
  const videoCount = (tutoringData.videotips ?? []).length;

  const fixedFinished = tutoringData.themeAssignments.filter(assignment => assignment.finished).length;
  const fixedCount = (tutoringData.themeAssignments ?? []).length;

  const othersFinished = (tutoringData.assignments ?? []).filter(assignment => assignment.finished).length;
  const othersCount = (tutoringData.assignments ?? []).length;

  return {
    videoCount,
    videoFinished,
    fixedCount,
    fixedFinished,
    othersCount,
    othersFinished,
  };
};

export const parseThemes = (
  userThemes: GetUserThemesQuery,
  tutoringThemes: UserTutoringMinimumFragment[],
  subjectId: string,
  email: string,
): ThemeRowDataType[] =>
  userThemes.userThemes
    .filter(theme => theme.theme?.subject === subjectId)
    .map(userTheme => {
      const tutoring = tutoringThemes.find(tutoring => tutoring.theme === userTheme.theme?.id);
      if (!tutoring) return undefined;

      const progress = countProgress(tutoring, email);

      const envelopes = {
        red: 0,
        green: 0,
        yellow: 0,
      };

      tutoring.assignments.forEach(assignment => {
        const foundAssignment = assignment.userAssignmentObject;
        if (!foundAssignment) return;

        switch (foundAssignment.envelope) {
          case "red":
            envelopes.red += 1;
            break;
          case "green":
            envelopes.green += 1;
            break;
          case "yellow":
            envelopes.yellow += 1;
            break;
        }
      });

      return {
        themeId: userTheme.theme?.id ?? undefined,
        tutoringId: tutoring.id,
        title: userTheme.theme?.appName ?? userTheme.theme?.name ?? "",
        description: userTheme.theme?.description ?? undefined,
        avgPoints: userTheme.theme?.avgTestPoints ?? undefined,
        themeProgress: progress,
        assignmentsRed: envelopes.red,
        assignmentsGreen: envelopes.green,
        assignmentsYellow: envelopes.yellow,
        isBadge: tutoring.userAchievements.length > 0,
      };
    })
    .filter(BooleanWithTypeGuard)
    .sort((a, b) => (b.avgPoints ?? 0) - (a.avgPoints ?? 0));
